import { ChartPieDataset, ChartBarDataset } from "pptxgenjs"
import * as AccountDomain from "../../../../../domain/sfp/account-domain"
import {
    accountIsSpecificAllocation,
    Allocation,
    allocationLabels
} from "../../../../../domain/sfp/allocation-domain"
import * as DemographyDomain from "../../../../../domain/sfp/demography-domain"
import * as TaxDomain from "../../../../../domain/sfp/tax-domain"
import * as Model from "../../../../../model"

export function createAllocationsChartData(
    allocations: Allocation[],
    observations: Model.Observation[],
    years: number[]
): ChartBarDataset[] {
    const labels = years.map(y => y.toString())

    return allocations.map<ChartBarDataset>(allocation => {
        const name = allocationLabels[allocation]
        const values = years.map<number>(year => {
            let value = 0
            for (const o of observations) {
                if (year !== o.year) continue
                if (!accountIsSpecificAllocation(o.account, allocation)) continue
                value += o.amount
            }
            return value
        })
        return { labels, name, values }
    })
}

export function createGlobalChartData(
    name: string,
    observations: Model.Observation[],
    year: number
): [ChartPieDataset] {
    const labels = [
        TaxDomain.TaxLabels.propertyTax,
        TaxDomain.TaxLabels.attribution,
        TaxDomain.TaxLabels.equalisationFund,
        TaxDomain.TaxLabels.additionnalTax,
        TaxDomain.TaxLabels.otherTax
    ]

    const values = observations.reduce(([
        proptertyTax,
        attributionTax,
        equalisationFund,
        additionnalTax,
        otherTax
    ], o) => {
        if (year === o.year) {
            switch (true) {
                case TaxDomain.accountIsAdditionnalTax(o.account):
                    additionnalTax += o.amount
                    break
                case TaxDomain.accountIsAttributionTax(o.account):
                    attributionTax += o.amount
                    break
                case TaxDomain.accountIsEqualisationFund(o.account):
                    equalisationFund += o.amount
                    break
                case TaxDomain.accountIsPropertyTax(o.account):
                    proptertyTax += o.amount
                    break
                case TaxDomain.accountIsTax(o.account):
                    otherTax += o.amount
                    break
            }
        }
        return [proptertyTax, attributionTax, equalisationFund, additionnalTax, otherTax]
    }, [0, 0, 0, 0, 0])

    return [{ labels, name, values }]
}

export function createPerInhabitantChartData(
    taxesChartData: ChartBarDataset[],
    demographies: Model.Demography[],
    referenceYear: number
): ChartBarDataset[] {
    return taxesChartData.map<ChartBarDataset>(dataset => {
        // copie le dataset
        const perInhabitantDataset: ChartBarDataset = {
            labels: dataset.labels.slice(),
            name: dataset.name,
            values: dataset.values.slice()
        }

        const recentYearDemography = demographies.length > 0 && demographies.reduce((max, dem) => (max.year > dem.year) ? max : dem);

        // pour chaque libellé (pour chaque année)
        perInhabitantDataset.labels.forEach((y, yearIndex) => {
            // conversion du libellé en valeur entière
            const year = parseInt(y)
            // recherche de la démographie par année
            const demography = DemographyDomain.findByYear(demographies, year) || (year > referenceYear && recentYearDemography)
            // division de la valeur par la population si elle existe
            perInhabitantDataset.values[yearIndex] = demography && demography.population ? perInhabitantDataset.values[yearIndex] / demography.population : 0
        })

        return perInhabitantDataset
    })
}

export function createPerTaxChartData(
    name: string,
    tax: TaxDomain.Tax,
    observations: Model.Observation[],
    year: number
): [ChartPieDataset] {
    const accounts = AccountDomain.sortByNumber(
        AccountDomain.distinctAccounts(observations)
    )
    const labels: string[] = []
    const values: number[] = []
    const dataset = accounts.reduce<ChartPieDataset>((dataset, account) => {
        if (TaxDomain.accountIsTaxType(account, tax)) {
            labels.push(`${account.number} - ${account.label}`)
            values.push(0)
            const index = values.length - 1
            for (const o of observations) {
                if (!AccountDomain.equal(account, o.account)) continue
                if (year !== o.year) continue
                values[index] += o.amount
            }
        }
        return dataset
    }, { labels, name, values })

    // Supprime les éléments dont la valeur est 0 ou indéfinie
    for (let i = dataset.values.length - 1; i >= 0; i--) {
        const value = dataset.values[i]

        if (!!value) continue

        dataset.labels.splice(i, 1)
        dataset.values.splice(i, 1)
    }

    return [dataset]
}

export function createPerTaxPerInhabitantChartData(
    name: string,
    tax: TaxDomain.Tax,
    observations: Model.Observation[],
    demographies: Model.Demography[],
    year: number
): [ChartPieDataset] {
    const perTaxChartData = createPerTaxChartData(null, tax, observations, year)
    const dataset: ChartPieDataset = {
        labels: perTaxChartData[0].labels.slice(),
        name,
        values: perTaxChartData[0].values.slice()
    }
    const demography = DemographyDomain.findByYear(demographies, year)

    dataset.values.forEach((value, valueIndex) => {
        dataset.values[valueIndex] = demography && demography.population
            ? value / demography.population : 0
    })

    return [dataset]
}

export function createTaxesChartData(
    taxes: TaxDomain.Tax[],
    observations: Model.Observation[],
    years: number[],
    scenariiTaxes: any[],
    accounts: Model.LocalAuthorityChapterAccount[],
    referenceYear: number
): ChartBarDataset[] {
    const labels = years.map(y => y.toString())

    return taxes.map<ChartBarDataset>(tax => {
        const name = TaxDomain.getTaxLabel(tax)
        const values: number[] = []

        years.forEach((year, yearIndex) => {
            values.push(0)

            for (const o of observations) {
                if (!TaxDomain.accountIsTaxType(o.account, tax)) continue
                if (year !== o.year) {
                    const obs = observations.find((ob) => ob.account.id === o.account.id && ob.year === referenceYear)
                    const anyScenarii = obs && scenariiTaxes.find((s) => s.account_id === obs.account.id)
                    const sameAccount = observations.some((ob) => ob.account.id === o.account.id && o.year < ob.year)

                    if (!anyScenarii && year > referenceYear && obs && !sameAccount) {
                        values[yearIndex] += obs.amount
                    }
                } else if (year === o.year) {
                    values[yearIndex] += o.amount
                }
            }

            for (const sce of scenariiTaxes) {
                if (year <= referenceYear) continue
                const account = accounts.find((o) => o.account.id === sce.account_id)
                if (account == null || account == undefined || !TaxDomain.accountIsTaxType(account.account, tax)) continue

                let amount = 0
                const sameAccount = scenariiTaxes.some((s) => s.account_id === sce.account_id && (s.year === year || s.year < sce.year))

                if (year === sce.year) amount = findValue(account.account.id, sce, scenariiTaxes, observations, year, referenceYear)
                else {
                    if (sameAccount) continue
                    amount = findValue(account.account.id, null, scenariiTaxes, observations, year, referenceYear)
                }

                values[yearIndex] += amount
            }

        })

        return { labels, name, values }
    })
}

function findValue(
    accountId: number,
    scenariiTax: any = null,
    scenariiTaxes: any[] = null,
    observations: Model.Observation[] = null,
    year: number,
    referenceYear: number
): number {
    let value
    let observation = observations.find((o) => o.account.id === accountId && o.year === referenceYear) || null
    let previousScenarii = scenariiTaxes.find((sce) => sce.account_id === accountId && sce.year === year - 1) || null

    if (year === referenceYear + 1) value = observation ? observation.amount : 0
    else {
        value = previousScenarii ? findValue(accountId, previousScenarii, scenariiTaxes, observations, year - 1, referenceYear) : findValue(accountId, null, scenariiTaxes, observations, year - 1, referenceYear)
    }

    if (scenariiTax) {
        if (scenariiTax.amount) {
            if (scenariiTax.percentile) {
                value += (value * scenariiTax.amount) / 100;
            } else {
                value = scenariiTax.amount
            }
        }
    }

    return value;
}

export function getProspectiveTaxes(
    scenario: Model.Scenario,
    scenarioYear: number
) {
    let scenariiTaxes = []
    scenario.years.forEach((scenarii, yearIndex) => {
        if (scenarii.year > scenarioYear) {
            for (const acc of scenarii.accounts) {
                if (acc.chapter_id === 73 && acc.receipts) {
                    const year = scenarii.year
                    const tax = { ...acc, year }
                    scenariiTaxes.push(tax)
                }
            }
        }
    })
    return scenariiTaxes
}


// WEBPACK FOOTER //
// ./src/component/management-report/generator/observation-generator/tax-generator/utils.ts